import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faAngleLeft, faAngleRight, faSpinner } from "@fortawesome/free-solid-svg-icons";
import apiServices from "../../../../services/apiService";
import storageService from "../../../../utils/storageService";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";
import CustomSelect from "../../../common/formfields/CustomSelect";
import { primaryColor, secondaryColor } from "../../../../utils/comon";

const today = new Date().toISOString().split('T')[0];

const formatDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const getMaxDate = () => {
    const localStart = new Date();
    localStart.setHours(0, 0, 0, 0);
    const max = new Date(localStart);
    max.setDate(localStart.getDate() + 180);
    return formatDate(max);
};

const PREDEFINED_ROLES = ['Maid', 'House Keeping', 'Driver'];

const StaffForm = () => {
    const { mobile } = useParams();
    const navigate = useNavigate();
    const isEditMode = mobile && mobile !== "add";

    const employeeName = storageService.getItem("employeeName") || "system";
    const employeeCode = storageService.getItem("employeeCode");
    const roleId = Number(storageService.getItem("roleId"));

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Site/unit info for role-based fields
    const [siteId, setSiteId] = useState(0);
    const [siteName, setSiteName] = useState('');
    const [unitId, setUnitId] = useState(0);
    const [unitName, setUnitName] = useState('');
    const [units, setUnits] = useState([]); // for role=3 dropdown

    const [actualRoleFromApi, setActualRoleFromApi] = useState('');

    const [formData, setFormData] = useState({
        staffId: 0,
        name: "",
        mobileNumber: "",
        address: "",
        bloodGroup: "",
        dateOfBirth: "",
        gender: "",
        idProofType: "",
        idProof: "",
        image: "",
        role: "",
        isActiveId: 1,
        siteId: 0,
        unitId: null,
        lastWorkingDate: getMaxDate()
    });

    // Guard: roleId 1 (Super Admin) and 5 (Tenant) cannot access the form
    useEffect(() => {
        if (roleId === 1 || roleId === 5) {
            navigate('/zentres/Staff-management/Staff-list');
        }
    }, [roleId, navigate]);

    // Setup role-based access - mirrors Angular setupRoleAccess()
    const setupRoleAccess = async () => {
        if (!employeeCode) return;

        try {
            const userRes = await apiServices({
                url: '/ResidentialUser/GetResidentialUserById',
                method: 'POST',
                data: { userId: employeeCode }
            });

            if (userRes.success && userRes.data?.data?.[0]) {
                const user = userRes.data.data[0];

                if (roleId === 3) {
                    // Site Admin: get siteId, fetch all units for dropdown
                    setSiteId(user.siteId);
                    setSiteName(user.siteName || '');
                    setFormData(prev => ({ ...prev, siteId: user.siteId }));

                    // Fetch units for site
                    const unitRes = await apiServices({
                        url: '/ResidentialUser/GetResidentialUserById',
                        method: 'POST',
                        data: { siteId: user.siteId }
                    });
                    if (unitRes.success && unitRes.data?.data) {
                        const uniqueUnits = Array.from(
                            new Map(unitRes.data.data.map(u => [u.unitId, u])).values()
                        );
                        setUnits(uniqueUnits);
                    }
                }

                if (roleId === 4) {
                    // Unit Admin: auto-fill both site and unit
                    setSiteId(user.siteId);
                    setUnitId(user.unitId);
                    setSiteName(user.siteName || '');
                    setUnitName(user.unitName || '');
                    setFormData(prev => ({ ...prev, siteId: user.siteId, unitId: user.unitId }));
                }
            }
        } catch (err) {
            console.error("Error fetching user details", err);
        }
    };

    useEffect(() => {
        const fetchStaffDetails = async () => {
            setLoading(true);
            try {
                if (isEditMode) {
                    const response = await apiServices({
                        method: "GET",
                        url: `/ResidentialStaffMembers/GetResidentialStaffDetailsByMobileNumber/${mobile}`
                    });

                    if (response?.success && response?.data?.data?.length > 0) {
                        const staff = { ...response.data.data[0] };

                        // Handle custom role - mirrors Angular predefined roles logic
                        if (!PREDEFINED_ROLES.includes(staff.role)) {
                            setActualRoleFromApi(staff.role);
                            staff.role = 'Other';
                        }

                        if (staff.dateOfBirth) {
                            staff.dateOfBirth = new Date(staff.dateOfBirth).toISOString().split('T')[0];
                        }

                        staff.lastWorkingDate = staff.lastWorkingDate
                            ? new Date(staff.lastWorkingDate).toISOString().split('T')[0]
                            : getMaxDate();

                        setFormData(prev => ({ ...prev, ...staff }));
                    } else {
                        toast.error("Failed to load staff details.");
                        navigate("/zentres/Staff-management/Staff-list");
                    }
                }
            } catch (error) {
                toast.error("Error fetching staff details");
            } finally {
                setLoading(false);
            }
        };

        setupRoleAccess();
        fetchStaffDetails();
    }, [mobile, isEditMode]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleSelectChange = (field, option) => {
        setFormData(prev => ({ ...prev, [field]: option?.value }));
    };

    const handleFileChange = (e, field) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                setFormData(prev => ({ ...prev, [field]: reader.result }));
            };
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name || !formData.mobileNumber) {
            toast.warning("Name and Mobile Number are required.");
            return;
        }

        if (formData.mobileNumber.length !== 10) {
            toast.warning("Mobile number must be exactly 10 digits.");
            return;
        }

        setSubmitting(true);

        try {
            let finalRole = formData.role;
            // Restore actual role if "Other" was selected in edit mode
            if (isEditMode && formData.role === 'Other' && actualRoleFromApi) {
                finalRole = actualRoleFromApi;
            }

            const finalSiteId = (roleId === 3 || roleId === 4) ? siteId : formData.siteId;
            const finalUnitId = roleId === 4 ? unitId : formData.unitId;

            const status = formData.isActiveId === 1 ? 'Active' : 'Inactive';

            if (isEditMode) {
                const lastWorkingDate = formData.isActiveId !== 1 ? today : formData.lastWorkingDate;

                const updateBody = {
                    staffId: formData.staffId,
                    name: formData.name,
                    role: finalRole,
                    image: formData.image,
                    mobileNumber: formData.mobileNumber,
                    address: formData.address,
                    idProof: formData.idProof,
                    idProofType: formData.idProofType,
                    bloodGroup: formData.bloodGroup,
                    gender: formData.gender,
                    dateOfBirth: formData.dateOfBirth,
                    siteId: finalSiteId,
                    unitId: finalUnitId,
                    lastWorkingDate,
                    createdOn: new Date().toISOString(),
                    isActiveId: 1,
                    createdBy: employeeName || 'system',
                    modifiedBy: employeeName || 'system',
                    modifiedOn: new Date().toISOString(),
                    status
                };

                const res = await apiServices({
                    method: "PUT",
                    url: `/ResidentialStaffMembers/UpdateResidentialStaffDetailsAsync/${finalSiteId}`,
                    data: updateBody
                });

                if (res?.success && (res.data?.isSuccess === true || res.data?.IsSuccess === true)) {
                    toast.success(res.data?.message || res.data?.Message || 'Updated successfully!');
                    navigate('/zentres/Staff-management/Staff-list');
                } else {
                    toast.error(res.data?.message || res.data?.Message || 'Update failed!');
                }
            } else {
                const createBody = {
                    ...formData,
                    role: finalRole,
                    siteId: finalSiteId,
                    unitId: finalUnitId,
                    lastWorkingDate: formData.lastWorkingDate,
                    isActiveId: 1,
                    status,
                    createdBy: employeeName || 'system'
                };

                const res = await apiServices({
                    method: "POST",
                    url: `/ResidentialStaffMembers/ResidentialStaffDetailsAsync`,
                    data: createBody
                });

                if (res?.success && (res.data?.isSuccess === true || res.data?.IsSuccess === true)) {
                    toast.success(res.data?.Message || res.data?.message || 'Created successfully!');
                    navigate('/zentres/Staff-management/Staff-list');
                } else {
                    toast.error(res.data?.Message || res.data?.message || 'Creation failed!');
                }
            }
        } catch (error) {
            console.error("Error saving staff:", error);
            toast.error("Error saving staff details");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-full w-full bg-white rounded-lg">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-500 text-3xl" />
            </div>
        );
    }

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs />

            <PageHeadingSection
                Navbar={true}
                mainHeading={`${isEditMode ? 'Edit' : 'Add'} Staff Member`}
                subHeading="Add or update staff member information including personal details, contact information, and role assignments."
            />

            <div className="mt-6 w-full max-w-5xl">
                <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100 flex flex-col gap-6 shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                        {/* Site - shown for role 3 and 4 (read-only/auto-filled) */}
                        {(roleId === 3 || roleId === 4) && (
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Site <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={siteName}
                                    disabled
                                    className="w-full px-3 py-2 bg-[#F8FAFC] border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
                                />
                            </div>
                        )}

                        {/* Unit dropdown - role 3 gets dropdown, role 4 gets readonly */}
                        {roleId === 3 && (
                            <div className="flex flex-col gap-2">
                                <CustomSelect
                                    label={<span className="text-xs font-bold text-gray-700 mb-1 block uppercase tracking-wider">Flat <span className="text-red-500">*</span></span>}
                                    options={units.map(u => ({ label: u.unitName, value: u.unitId }))}
                                    value={formData.unitId ? { label: units.find(u => u.unitId === formData.unitId)?.unitName || formData.unitId, value: formData.unitId } : null}
                                    onChange={(opt) => handleSelectChange("unitId", opt)}
                                    placeholder="Select Flat"
                                />
                            </div>
                        )}

                        {roleId === 4 && (
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Flat <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={unitName}
                                    disabled
                                    className="w-full px-3 py-2 bg-[#F8FAFC] border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
                                />
                            </div>
                        )}

                        {/* Name */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="name">Name <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                id="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Enter name"
                                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
                                required
                            />
                        </div>

                        {/* Mobile Number */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="mobileNumber">Mobile Number <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                id="mobileNumber"
                                value={formData.mobileNumber}
                                onChange={handleInputChange}
                                placeholder="10 digit mobile number"
                                maxLength="10"
                                disabled={isEditMode}
                                className={`w-full px-3 py-2 border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400 ${isEditMode ? 'bg-[#F8FAFC] cursor-not-allowed text-gray-500' : 'bg-white'}`}
                                required
                            />
                        </div>

                        {/* Address */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="address">Address <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                id="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                placeholder="Enter address"
                                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
                            />
                        </div>

                        {/* Blood Group */}
                        <div className="flex flex-col gap-2">
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 mb-1 block uppercase tracking-wider">Blood Group</span>}
                                options={[
                                    { label: 'A+', value: 'A+' }, { label: 'A-', value: 'A-' },
                                    { label: 'B+', value: 'B+' }, { label: 'B-', value: 'B-' },
                                    { label: 'AB+', value: 'AB+' }, { label: 'AB-', value: 'AB-' },
                                    { label: 'O+', value: 'O+' }, { label: 'O-', value: 'O-' }
                                ]}
                                value={formData.bloodGroup ? { label: formData.bloodGroup, value: formData.bloodGroup } : null}
                                onChange={(opt) => handleSelectChange("bloodGroup", opt)}
                                placeholder="Select Blood Group"
                            />
                        </div>

                        {/* Date of Birth */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="dateOfBirth">Date of Birth</label>
                            <input
                                type="date"
                                id="dateOfBirth"
                                value={formData.dateOfBirth}
                                onChange={handleInputChange}
                                max={today}
                                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                            />
                        </div>

                        {/* Gender */}
                        <div className="flex flex-col gap-2">
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 mb-1 block uppercase tracking-wider">Gender <span className="text-red-500">*</span></span>}
                                options={[
                                    { label: 'Male', value: 'Male' },
                                    { label: 'Female', value: 'Female' },
                                    { label: 'Other', value: 'Other' }
                                ]}
                                value={formData.gender ? { label: formData.gender, value: formData.gender } : null}
                                onChange={(opt) => handleSelectChange("gender", opt)}
                                placeholder="Select Gender"
                            />
                        </div>

                        {/* Role */}
                        <div className="flex flex-col gap-2">
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 mb-1 block uppercase tracking-wider">Role <span className="text-red-500">*</span></span>}
                                options={[
                                    { label: 'Maid', value: 'Maid' },
                                    { label: 'House Keeping', value: 'House Keeping' },
                                    { label: 'Driver', value: 'Driver' },
                                    { label: 'Other', value: 'Other' }
                                ]}
                                value={formData.role ? { label: formData.role, value: formData.role } : null}
                                onChange={(opt) => handleSelectChange("role", opt)}
                                placeholder="Select Role"
                            />
                        </div>

                        {/* ID Proof Type */}
                        <div className="flex flex-col gap-2">
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 mb-1 block uppercase tracking-wider">ID Proof Type <span className="text-red-500">*</span></span>}
                                options={[
                                    { label: 'Aadhar Card', value: 'AadharCard' },
                                    { label: 'PAN Card', value: 'PanCard' },
                                    { label: 'Voter ID', value: 'VoterID' },
                                    { label: 'Driving License', value: 'Driving license' },
                                    { label: 'Passport', value: 'Passport' },
                                    { label: 'Other', value: 'Other' }
                                ]}
                                value={formData.idProofType ? { label: formData.idProofType, value: formData.idProofType } : null}
                                onChange={(opt) => handleSelectChange("idProofType", opt)}
                                placeholder="Select ID Proof Type"
                            />
                        </div>

                        {/* Valid Up To / Last Working Date */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="lastWorkingDate">Valid Up To <span className="text-red-500">*</span></label>
                            <input
                                type="date"
                                id="lastWorkingDate"
                                value={formData.lastWorkingDate}
                                onChange={handleInputChange}
                                min={today}
                                max={getMaxDate()}
                                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                            />
                        </div>

                        {/* Active Status */}
                        <div className="flex flex-col gap-2">
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 mb-1 block uppercase tracking-wider">Active Status <span className="text-red-500">*</span></span>}
                                options={[
                                    { label: 'Active', value: 1 },
                                    { label: 'Inactive', value: 0 }
                                ]}
                                value={{ label: formData.isActiveId === 1 ? 'Active' : 'Inactive', value: formData.isActiveId }}
                                onChange={(opt) => handleSelectChange("isActiveId", opt)}
                                placeholder="Select Status"
                            />
                        </div>

                        {/* ID Proof Upload */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block">ID Proof <span className="text-red-500">*</span></label>
                            <div className="relative group cursor-pointer w-full h-32 rounded-xl border-2 border-dashed border-gray-300 hover:border-blue-500 transition-colors bg-white flex items-center justify-center overflow-hidden">
                                {formData.idProof ? (
                                    <img src={formData.idProof} alt="ID Proof Preview" className="w-full h-full object-contain p-2" />
                                ) : (
                                    <div className="flex flex-col items-center justify-center text-gray-400 group-hover:text-blue-500 transition-colors">
                                        <FontAwesomeIcon icon={faCamera} className="text-2xl mb-1" />
                                        <span className="text-[10px] font-bold text-center px-4 uppercase tracking-wide">Upload ID Proof</span>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleFileChange(e, "idProof")}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                            </div>
                        </div>

                        {/* Staff Image Upload */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block">Staff Image <span className="text-red-500">*</span></label>
                            <div className="relative group cursor-pointer w-full h-32 rounded-xl border-2 border-dashed border-gray-300 hover:border-blue-500 transition-colors bg-white flex items-center justify-center overflow-hidden">
                                {formData.image ? (
                                    <img src={formData.image} alt="Staff Preview" className="w-full h-full object-contain p-2" />
                                ) : (
                                    <div className="flex flex-col items-center justify-center text-gray-400 group-hover:text-blue-500 transition-colors">
                                        <FontAwesomeIcon icon={faCamera} className="text-2xl mb-1" />
                                        <span className="text-[10px] font-bold text-center px-4 uppercase tracking-wide">Upload Photo</span>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleFileChange(e, "image")}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                            </div>
                        </div>

                    </div>
                </div>

                {/* Footer Actions */}
                <div className="flex justify-between items-center mt-6 pt-6 mb-8">
                    <button
                        type="button"
                        onClick={() => navigate("/zentres/Staff-management/Staff-list")}
                        className="px-6 py-2.5 border border-gray-200 rounded-xl text-xs font-bold flex items-center gap-2 text-gray-600 hover:bg-gray-50 active:scale-95 transition-all shadow-sm"
                    >
                        <FontAwesomeIcon icon={faAngleLeft} />
                        Cancel
                    </button>

                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="px-8 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200 disabled:opacity-50"
                        style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                        onMouseEnter={(e) => {
                            if (!submitting) {
                                e.currentTarget.style.backgroundColor = primaryColor();
                                e.currentTarget.style.color = '#FFFFFF';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!submitting) {
                                e.currentTarget.style.backgroundColor = secondaryColor();
                                e.currentTarget.style.color = primaryColor();
                            }
                        }}
                    >
                        {submitting ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEditMode ? 'Update Staff' : 'Add Staff')}
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StaffForm;
